// HostSensorsSection — the HOST-WIDE kernel_sensor editor, folded into the
// Policy page as its "Stage 4 · Host-wide" surface.
//
// kernel_sensor is host-global (a singleton), NOT per-tier policy content
// (ADR-0005), so this section owns its OWN state and saves via the separate
// REST endpoint GET/PUT /api/v1/host-sensors — never through the per-tier
// policy PUT (which would fail DisallowUnknownFields). It renders in the same
// design language as the rest of Policy: corenet PolicyModeControl (off/alert/
// block) + ChipEditor, serif headings, the Section grey-out idiom.
import { useEffect, useMemo, useState } from "react";
import { App as AntApp, Button, Flex, Input, Space, Switch, Tag, Typography, theme } from "antd";
import { PolicyModeControl, ChipEditor, type PolicyMode } from "@bluerock/corenet/policy";
import { useAuth } from "@/lib/auth/AuthProvider";
import { newIdempotencyKey } from "@/lib/api/client";
import { KERNEL_RULES } from "@/lib/kernelInfo";

const { Text } = Typography;

const HOST_SENSORS_URL = `${import.meta.env.BASE_URL}api/v1/host-sensors`;

// ── Types (mirror api/internal/policy HostKernel JSON) ──────────────────────
type Toggle = { enable?: boolean; remediate?: boolean };
type ProcessGroup = { restricted: string[]; executable: string[] };
type SocketSelector = { path_pattern?: string; comm_list?: string[]; allowed_addresses?: Record<string, number[]> };
type ContainerSelector = { image: string; container: string; namespace?: string; deployment?: string; pod?: string };
type CapsAllowEntry = { container_exception_list?: ContainerSelector[]; allowed_caps: string[] };
type ContainerDrift = Toggle & {
  file_drift?: boolean;
  library_drift?: boolean;
  process_exception_list?: string[];
  interpreters?: Record<string, string[]>;
};
export type HostKernel = {
  process_restriction?: Toggle & { process_groups?: ProcessGroup[] };
  socket_detection?: Toggle & { process_address_list?: SocketSelector[] };
  container_capabilities?: Toggle & { caps_denied?: string[]; explicit_allow?: CapsAllowEntry[] };
  container_drift?: ContainerDrift;
  toggles?: Record<string, Toggle>;
};

// The four sensors with structured lists; the rest are enable/remediate only.
// process_guard / sensitive_file_access have per-tier siblings on this page
// (Processes, Files); kernel_integrity is always-on — all excluded here.
const STRUCTURED = new Set(["process_restriction", "socket_detection", "container_capabilities", "container_drift"]);
const TOGGLE_SENSORS = new Set([
  "userspace_force_nx_stack", "remote_shell_control", "nsenter", "container_socket_protect",
  "rogue_container", "mmap_exec_file", "detect_setugid", "forced_mem_access", "fileless_exec", "system_visibility",
]);
const infoFor = (key: string) => KERNEL_RULES.find((r) => r.key === key);

// Thematic groups (mirrors the design mockup's kernel surface layout).
const GROUPS: { title: string; keys: string[] }[] = [
  { title: "Integrity & memory", keys: ["forced_mem_access", "mmap_exec_file", "userspace_force_nx_stack"] },
  { title: "Credentials & escalation", keys: ["detect_setugid", "process_restriction"] },
  { title: "Execution & drivers", keys: ["remote_shell_control", "fileless_exec", "nsenter"] },
  { title: "Network & filesystem", keys: ["socket_detection"] },
  { title: "Containers", keys: ["container_drift", "container_capabilities", "rogue_container", "container_socket_protect"] },
  { title: "Visibility", keys: ["system_visibility"] },
];

type Mode = "off" | "detect" | "enforce";
const toPM = (m: Mode): PolicyMode => (m === "enforce" ? "block" : m === "detect" ? "alert" : "off");
const fromPM = (m: PolicyMode): Mode => (m === "block" ? "enforce" : m === "alert" ? "detect" : "off");

// Mode read/write dispatch: structured sensors are top-level fields; toggle
// sensors live under a `toggles` map.
function readToggle(hk: HostKernel, key: string): Toggle {
  if (STRUCTURED.has(key)) return (hk as Record<string, Toggle>)[key] ?? {};
  return hk.toggles?.[key] ?? {};
}
function modeOf(t: Toggle, canBlock: boolean): Mode {
  if (!t.enable) return "off";
  return t.remediate && canBlock ? "enforce" : "detect";
}
function applyMode(hk: HostKernel, key: string, mode: Mode): HostKernel {
  const next: HostKernel = structuredClone(hk);
  const t: Toggle = mode === "off" ? { enable: false, remediate: false }
    : mode === "detect" ? { enable: true, remediate: false }
    : { enable: true, remediate: true };
  if (STRUCTURED.has(key)) {
    const existing = ((next as Record<string, Toggle>)[key] ?? {}) as Toggle;
    (next as Record<string, unknown>)[key] = { ...existing, ...t };
  } else {
    next.toggles = { ...(next.toggles ?? {}), [key]: t };
  }
  return next;
}

// Posture summary for the "Stage 4" header — counts across the managed set.
export function hostPosture(hk: HostKernel): { on: number; total: number } {
  const keys = KERNEL_RULES.filter((r) => STRUCTURED.has(r.key) || TOGGLE_SENSORS.has(r.key));
  const on = keys.filter((r) => readToggle(hk, r.key).enable).length;
  return { on, total: keys.length };
}

// Posture as colored text + mono glyph, matching Policy's ModeTag.
function ModeTag({ mode }: { mode: Mode }) {
  const m =
    mode === "enforce"
      ? { label: "Blocking", cls: "text-danger", glyph: "●" }
      : mode === "detect"
        ? { label: "Observe", cls: "text-warning", glyph: "◐" }
        : { label: "Off", cls: "text-text-muted", glyph: "○" };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${m.cls}`}>
      <span className="font-mono text-[10px] leading-none">{m.glyph}</span>
      {m.label}
    </span>
  );
}

// ── the section ─────────────────────────────────────────────────────────────
export default function HostSensorsSection({ editing }: { editing: boolean }) {
  const { token } = theme.useToken();
  const { token: authToken } = useAuth();
  const { message } = AntApp.useApp();
  const [hk, setHk] = useState<HostKernel>({});
  const [saved, setSaved] = useState("{}");
  // Retained from GET/PUT's `{content, version}` response — not sent back on
  // PUT (the endpoint doesn't take a version for optimistic concurrency),
  // but worth keeping around rather than silently discarding it: it's the
  // one thing that tells you which deploy you're actually looking at.
  const [version, setVersion] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const authHeaders = useMemo(
    () => (authToken ? { Authorization: `Bearer ${authToken}` } : {}) as Record<string, string>,
    [authToken],
  );

  // Parses an RFC 7807 problem+json error body — `detail` (falling back to
  // `code`/status), plus `request_id` when present (500s are deliberately
  // generic, so it's the only thing to hand support). Distinguishes the two
  // named codes this endpoint documents (INVALID_HOST_KERNEL, MISSING_CONTENT)
  // by just surfacing whatever `detail`/`code` the server actually sent,
  // rather than the raw response text.
  async function explainHostSensorsError(res: Response, fallback: string): Promise<string> {
    try {
      const body = await res.json();
      const message = typeof body.detail === "string" ? body.detail : `${fallback} (${body.code ?? res.status})`;
      return typeof body.request_id === "string" ? `${message} (ref: ${body.request_id})` : message;
    } catch {
      return `${fallback} (${res.status})`;
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(HOST_SENSORS_URL, { headers: authHeaders });
        if (!res.ok) throw new Error(await explainHostSensorsError(res, "Could not load host sensors"));
        const body = await res.json();
        const content: HostKernel = body.content ?? {};
        setHk(content);
        setSaved(JSON.stringify(content));
        setVersion(typeof body.version === "number" ? body.version : null);
      } catch (e) {
        message.error(`Failed to load host sensors: ${(e as Error).message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeaders, message]);

  const dirty = JSON.stringify(hk) !== saved;

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(HOST_SENSORS_URL, {
        method: "PUT",
        headers: { ...authHeaders, "Content-Type": "application/json", "Idempotency-Key": newIdempotencyKey() },
        body: JSON.stringify({ content: hk }),
      });
      if (!res.ok) throw new Error(await explainHostSensorsError(res, "Could not save host sensors"));
      const body = await res.json();
      const content: HostKernel = body.content ?? {};
      const nextVersion = typeof body.version === "number" ? body.version : null;
      setHk(content);
      setSaved(JSON.stringify(content));
      setVersion(nextVersion);
      message.success(nextVersion !== null ? `Host sensors deployed (v${nextVersion})` : "Host sensors deployed");
    } catch (e) {
      message.error(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const setMode = (key: string, m: Mode) => setHk((prev) => applyMode(prev, key, m));

  const renderRow = (key: string) => {
    const info = infoFor(key);
    if (!info) return null;
    const mode = modeOf(readToggle(hk, key), info.canBlock);
    const structured = STRUCTURED.has(key);
    const showEditor = editing && structured && mode !== "off";
    return (
      <div key={key} style={{ padding: "12px 0", borderTop: `1px solid ${token.colorFillQuaternary}` }}>
        <Flex align="center" justify="space-between" gap={12}>
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{info.label}</div>
            <Text type="secondary" style={{ fontFamily: token.fontFamilyCode, fontSize: 10 }}>{info.key}</Text>
          </div>
          {editing ? (
            <PolicyModeControl
              size="small"
              supportsBlock={info.canBlock}
              value={toPM(mode)}
              onChange={(m) => setMode(key, fromPM(m))}
              aria-label={`${info.label} mode`}
            />
          ) : (
            <ModeTag mode={mode} />
          )}
        </Flex>
        <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 3, maxWidth: 720 }}>{info.blurb}</Text>
        {showEditor && <div style={{ marginTop: 8 }}>{structuredEditor(key, hk, setHk)}</div>}
      </div>
    );
  };

  const posture = hostPosture(hk);

  return (
    <div>
      <Flex align="flex-start" gap={12} style={{ background: token.colorFillQuaternary, padding: "12px 24px", borderTop: `1px solid ${token.colorBorderSecondary}` }}>
        <span style={{ marginTop: 2, display: "flex", height: 20, width: 20, flexShrink: 0, alignItems: "center", justifyContent: "center", borderRadius: "50%", background: token.colorPrimary, fontSize: 11, fontWeight: 600, color: "#fff" }}>4</span>
        <div style={{ flex: 1 }}>
          <Flex align="center" gap={8} wrap>
            <Text strong style={{ fontFamily: "var(--font-serif, 'Source Serif 4 Variable', serif)", fontSize: 14 }}>Host-wide sensors</Text>
            <Tag color="purple" style={{ marginInlineStart: 0 }}>system-wide</Tag>
            {!loading && <Text type="secondary" style={{ fontSize: 12 }}>{posture.on}/{posture.total} on</Text>}
            {version !== null && <Text type="secondary" style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>v{version}</Text>}
          </Flex>
          <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
            Kernel-level (LSM) protections for the WHOLE host — every sandbox, container, and process, regardless of policy tier. Blocks via EPERM. Admin-only; applies immediately across the host.
          </Text>
        </div>
        {editing && (
          <Space>
            {dirty && <Tag color="orange">unsaved</Tag>}
            <Button type="primary" size="small" loading={saving} disabled={!dirty} onClick={save}>Save host sensors</Button>
          </Space>
        )}
      </Flex>

      <div style={{ padding: "4px 24px 16px" }}>
        {editing && (
          <Text style={{ display: "block", color: token.colorWarning, fontSize: 12, margin: "8px 0" }}>
            Run a rule in Observe before switching it to Block — blocking an unobserved rule under load has caused kernel panics in production.
          </Text>
        )}
        {GROUPS.map((g) => (
          <div key={g.title} style={{ marginTop: 14 }}>
            <Text strong style={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: token.colorTextTertiary }}>{g.title}</Text>
            {g.keys.map(renderRow)}
          </div>
        ))}
      </div>
    </div>
  );
}

// Dispatch to the right structured editor for a sensor.
function structuredEditor(key: string, hk: HostKernel, setHk: React.Dispatch<React.SetStateAction<HostKernel>>) {
  switch (key) {
    case "process_restriction":
      return (
        <ProcessGroupsEditor
          value={hk.process_restriction?.process_groups ?? []}
          onChange={(groups) => setHk((p) => ({ ...p, process_restriction: { ...(p.process_restriction ?? {}), process_groups: groups } }))}
        />
      );
    case "socket_detection":
      return (
        <SocketDetectionEditor
          value={hk.socket_detection?.process_address_list ?? []}
          onChange={(list) => setHk((p) => ({ ...p, socket_detection: { ...(p.socket_detection ?? {}), process_address_list: list } }))}
        />
      );
    case "container_capabilities":
      return (
        <ContainerCapabilitiesEditor
          capsDenied={hk.container_capabilities?.caps_denied ?? []}
          explicitAllow={hk.container_capabilities?.explicit_allow ?? []}
          onCapsDenied={(v) => setHk((p) => ({ ...p, container_capabilities: { ...(p.container_capabilities ?? {}), caps_denied: v } }))}
          onExplicitAllow={(v) => setHk((p) => ({ ...p, container_capabilities: { ...(p.container_capabilities ?? {}), explicit_allow: v } }))}
        />
      );
    case "container_drift":
      return (
        <ContainerDriftEditor
          value={hk.container_drift ?? {}}
          onChange={(patch) => setHk((p) => ({ ...p, container_drift: { ...(p.container_drift ?? {}), ...patch } }))}
        />
      );
    default:
      return null;
  }
}

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 4 }}>{children}</Text>
);

// ── process_restriction.process_groups ──────────────────────────────────────
function ProcessGroupsEditor({ value, onChange }: { value: ProcessGroup[]; onChange: (g: ProcessGroup[]) => void }) {
  const { token } = theme.useToken();
  const update = (i: number, patch: Partial<ProcessGroup>) => onChange(value.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="small">
      {value.map((g, i) => (
        <div key={i} style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: 8 }}>
          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Text strong style={{ fontSize: 12 }}>Group {i + 1}</Text>
            <Button size="small" danger type="text" onClick={() => onChange(value.filter((_, idx) => idx !== i))}>Remove</Button>
          </Flex>
          <Flex gap={12} wrap>
            <div style={{ flex: 1, minWidth: 240 }}>
              <FieldLabel>Restricted (parent processes)</FieldLabel>
              <ChipEditor value={g.restricted} onChange={(v) => update(i, { restricted: v })} placeholder="**/nginx" />
            </div>
            <div style={{ flex: 1, minWidth: 240 }}>
              <FieldLabel>Forbidden executables (children)</FieldLabel>
              <ChipEditor value={g.executable} onChange={(v) => update(i, { executable: v })} placeholder="**/sh" />
            </div>
          </Flex>
          {(g.restricted.length === 0 || g.executable.length === 0) && (
            <Text type="warning" style={{ fontSize: 11 }}>A group needs at least one restricted process and one forbidden executable.</Text>
          )}
        </div>
      ))}
      <Button size="small" type="dashed" onClick={() => onChange([...value, { restricted: [], executable: [] }])}>+ Add group</Button>
    </Space>
  );
}

// ── socket_detection.process_address_list ───────────────────────────────────
function SocketDetectionEditor({ value, onChange }: { value: SocketSelector[]; onChange: (v: SocketSelector[]) => void }) {
  const { token } = theme.useToken();
  const update = (i: number, patch: Partial<SocketSelector>) => onChange(value.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  return (
    <Space direction="vertical" style={{ width: "100%" }} size="small">
      {value.map((s, i) => (
        <div key={i} style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: 8 }}>
          <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
            <Text strong style={{ fontSize: 12 }}>Rule {i + 1}</Text>
            <Button size="small" danger type="text" onClick={() => onChange(value.filter((_, idx) => idx !== i))}>Remove</Button>
          </Flex>
          <Flex gap={12} wrap>
            <div style={{ flex: 1, minWidth: 220 }}>
              <FieldLabel>Process path pattern</FieldLabel>
              <Input size="small" placeholder="**/curl" value={s.path_pattern} onChange={(e) => update(i, { path_pattern: e.target.value })} />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <FieldLabel>Comm list (process names)</FieldLabel>
              <ChipEditor value={s.comm_list ?? []} onChange={(v) => update(i, { comm_list: v })} placeholder="curl" />
            </div>
          </Flex>
          <div style={{ marginTop: 6 }}>
            <FieldLabel>Allowed addresses (CIDR → ports)</FieldLabel>
            <MapListEditor
              value={s.allowed_addresses ?? {}}
              keyLabel="10.0.0.0/8"
              valLabel="443"
              parseVals={(vals) => vals.map((v) => parseInt(v, 10)).filter((n) => Number.isInteger(n) && n >= 0 && n <= 65535)}
              renderVals={(ports) => (ports as number[]).map(String)}
              onChange={(a) => update(i, { allowed_addresses: a as Record<string, number[]> })}
            />
          </div>
        </div>
      ))}
      <Button size="small" type="dashed" onClick={() => onChange([...value, {}])}>+ Add rule</Button>
    </Space>
  );
}

// ── container_capabilities ──────────────────────────────────────────────────
function ContainerCapabilitiesEditor({
  capsDenied, explicitAllow, onCapsDenied, onExplicitAllow,
}: {
  capsDenied: string[]; explicitAllow: CapsAllowEntry[];
  onCapsDenied: (v: string[]) => void; onExplicitAllow: (v: CapsAllowEntry[]) => void;
}) {
  const { token } = theme.useToken();
  const updEntry = (i: number, patch: Partial<CapsAllowEntry>) => onExplicitAllow(explicitAllow.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const selList = (e: CapsAllowEntry) => e.container_exception_list ?? [];
  return (
    <div>
      <FieldLabel>Denied capabilities (host-wide)</FieldLabel>
      <ChipEditor value={capsDenied} onChange={onCapsDenied} placeholder="CAP_SYS_ADMIN" />
      <div style={{ height: 10 }} />
      <Text strong style={{ fontSize: 12 }}>Explicit allow entries</Text>
      <Space direction="vertical" style={{ width: "100%", marginTop: 4 }} size="small">
        {explicitAllow.map((e, ei) => (
          <div key={ei} style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: 8 }}>
            <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
              <Text strong style={{ fontSize: 12 }}>Entry {ei + 1}</Text>
              <Button size="small" danger type="text" onClick={() => onExplicitAllow(explicitAllow.filter((_, idx) => idx !== ei))}>Remove</Button>
            </Flex>
            <FieldLabel>Allowed caps (required)</FieldLabel>
            <ChipEditor value={e.allowed_caps} onChange={(v) => updEntry(ei, { allowed_caps: v })} placeholder="CAP_NET_ADMIN" />
            {e.allowed_caps.length === 0 && <Text type="warning" style={{ fontSize: 11, display: "block", marginTop: 4 }}>At least one allowed cap is required.</Text>}
            <div style={{ height: 8 }} />
            <FieldLabel>Container exception list</FieldLabel>
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              {selList(e).map((sel, si) => (
                <Flex key={si} gap={6} wrap align="center">
                  <Input size="small" style={{ width: 150 }} placeholder="image (req)" value={sel.image} onChange={(ev) => updEntry(ei, { container_exception_list: selList(e).map((x, idx) => (idx === si ? { ...x, image: ev.target.value } : x)) })} />
                  <Input size="small" style={{ width: 130 }} placeholder="container (req)" value={sel.container} onChange={(ev) => updEntry(ei, { container_exception_list: selList(e).map((x, idx) => (idx === si ? { ...x, container: ev.target.value } : x)) })} />
                  <Input size="small" style={{ width: 110 }} placeholder="namespace" value={sel.namespace} onChange={(ev) => updEntry(ei, { container_exception_list: selList(e).map((x, idx) => (idx === si ? { ...x, namespace: ev.target.value } : x)) })} />
                  <Input size="small" style={{ width: 110 }} placeholder="deployment" value={sel.deployment} onChange={(ev) => updEntry(ei, { container_exception_list: selList(e).map((x, idx) => (idx === si ? { ...x, deployment: ev.target.value } : x)) })} />
                  <Input size="small" style={{ width: 90 }} placeholder="pod" value={sel.pod} onChange={(ev) => updEntry(ei, { container_exception_list: selList(e).map((x, idx) => (idx === si ? { ...x, pod: ev.target.value } : x)) })} />
                  <Button size="small" danger type="text" onClick={() => updEntry(ei, { container_exception_list: selList(e).filter((_, idx) => idx !== si) })}>×</Button>
                </Flex>
              ))}
              <Button size="small" type="dashed" onClick={() => updEntry(ei, { container_exception_list: [...selList(e), { image: "", container: "" }] })}>+ Add selector</Button>
            </Space>
            {selList(e).some((s) => !s.image || !s.container) && <Text type="warning" style={{ fontSize: 11 }}>Each selector requires image and container.</Text>}
          </div>
        ))}
        <Button size="small" type="dashed" onClick={() => onExplicitAllow([...explicitAllow, { allowed_caps: [] }])}>+ Add allow entry</Button>
      </Space>
    </div>
  );
}

// ── container_drift ─────────────────────────────────────────────────────────
function ContainerDriftEditor({ value, onChange }: { value: ContainerDrift; onChange: (patch: Partial<ContainerDrift>) => void }) {
  return (
    <div>
      <Flex gap={20} align="center" style={{ marginBottom: 10 }}>
        <Space size={6}><Switch size="small" checked={!!value.file_drift} onChange={(c) => onChange({ file_drift: c })} /><Text style={{ fontSize: 12 }}>File drift</Text></Space>
        <Space size={6}><Switch size="small" checked={!!value.library_drift} onChange={(c) => onChange({ library_drift: c })} /><Text style={{ fontSize: 12 }}>Library drift</Text></Space>
      </Flex>
      <FieldLabel>Process exception list</FieldLabel>
      <ChipEditor value={value.process_exception_list ?? []} onChange={(v) => onChange({ process_exception_list: v })} placeholder="**/python3" />
      <div style={{ height: 10 }} />
      <FieldLabel>Interpreters (name → flags)</FieldLabel>
      <MapListEditor
        value={value.interpreters ?? {}}
        keyLabel="python"
        valLabel="-c"
        parseVals={(vals) => vals}
        renderVals={(flags) => flags as string[]}
        onChange={(v) => onChange({ interpreters: v as Record<string, string[]> })}
      />
    </div>
  );
}

// ── generic map<string, T[]> editor (CIDR→ports, interpreter→flags) ──────────
function MapListEditor({
  value, keyLabel, valLabel, parseVals, renderVals, onChange,
}: {
  value: Record<string, unknown[]>;
  keyLabel: string;
  valLabel: string;
  parseVals: (vals: string[]) => unknown[];
  renderVals: (vals: unknown[]) => string[];
  onChange: (v: Record<string, unknown[]>) => void;
}) {
  const [rows, setRows] = useState<Array<{ k: string; v: unknown[] }>>(() => Object.entries(value).map(([k, v]) => ({ k, v })));
  const push = (next: Array<{ k: string; v: unknown[] }>) => {
    setRows(next);
    const rec: Record<string, unknown[]> = {};
    for (const r of next) if (r.k.trim()) rec[r.k.trim()] = r.v;
    onChange(rec);
  };
  const setRow = (i: number, patch: Partial<{ k: string; v: unknown[] }>) => push(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  return (
    <Space direction="vertical" style={{ width: "100%" }} size={4}>
      {rows.map((r, i) => (
        <Flex key={i} gap={6} align="flex-start">
          <Input size="small" style={{ width: 170 }} placeholder={keyLabel} value={r.k} onChange={(e) => setRow(i, { k: e.target.value })} />
          <div style={{ flex: 1 }}><ChipEditor value={renderVals(r.v)} onChange={(vals) => setRow(i, { v: parseVals(vals) })} placeholder={valLabel} /></div>
          <Button size="small" danger type="text" onClick={() => push(rows.filter((_, idx) => idx !== i))}>×</Button>
        </Flex>
      ))}
      <Button size="small" type="dashed" onClick={() => push([...rows, { k: "", v: [] }])}>+ Add</Button>
    </Space>
  );
}
